/**
 * Batch 18-J9 — Training Providers Admin Panel
 *
 * Operational UI for the I3/J-lane provider evidence callback
 * registry. Renders inside `TrainingCatalogAdmin` under the
 * `Training Providers` tab. Backed entirely by the J3 read RPC +
 * J7 active-state / secret-rotation mutation RPCs. Never reads
 * provider base tables directly. Never displays raw secret
 * material, raw secret references, raw event payload, payload
 * hash, signature status, or idempotency tokens.
 *
 * Scope:
 *   - list providers via `list_training_providers_for_admin`
 *   - toggle active state via `set_training_provider_active_state`
 *   - rotate HMAC secret reference via
 *     `rotate_training_provider_secret_ref`
 *   - render integration instructions (endpoint + header names +
 *     provider key + a placeholder payload skeleton)
 *
 * Out of scope (will land in later J-lane batches):
 *   - provider registry CRUD (create / update / delete)
 *   - artifact mapping UI
 *   - user mapping UI
 *   - unresolved events UI
 *   - audit history UI
 *   - signed JWT key management
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertTriangle, KeyRound, Loader2, Search } from 'lucide-react';

// ─── Types (narrow local cast — generated Supabase types do not
//     yet know about the J3/J7 RPCs) ──────────────────────────────────

export interface TrainingProviderAdminRow {
  provider_id: string;
  org_id: string;
  shop_id: string | null;
  provider_key: string;
  display_name: string;
  provider_type: string;
  auth_type: 'hmac_sha256' | 'signed_jwt' | 'manual_import' | string;
  timestamp_tolerance_seconds: number;
  is_active: boolean;
  secret_configured: boolean;
  public_key_configured: boolean;
  created_at: string;
  updated_at: string;
  last_received_at: string | null;
  event_count: number | string;
  has_unmatched_events: boolean;
}

interface ListProvidersRpc {
  (
    fn: 'list_training_providers_for_admin',
    args: {
      p_org_id: string;
      p_shop_id: string | null;
      p_include_inactive: boolean;
    },
  ): Promise<{
    data: TrainingProviderAdminRow[] | null;
    error: { message: string; code?: string } | null;
  }>;
}

interface SetActiveStateRpc {
  (
    fn: 'set_training_provider_active_state',
    args: {
      p_provider_id: string;
      p_is_active: boolean;
      p_actor_user_id: string;
      p_reason: string | null;
      p_override_pending_jobs: boolean;
    },
  ): Promise<{
    data: unknown;
    error: { message: string; code?: string } | null;
  }>;
}

interface RotateSecretRpc {
  (
    fn: 'rotate_training_provider_secret_ref',
    args: {
      p_provider_id: string;
      p_new_secret_ref: string;
      p_overlap_window_seconds: number;
      p_actor_user_id: string;
      p_reason: string | null;
    },
  ): Promise<{
    data: unknown;
    error: { message: string; code?: string } | null;
  }>;
}

interface TrainingProvidersAdminPanelProps {
  orgId: string | null | undefined;
  shopId: string | null | undefined;
  currentUserId: string | null | undefined;
}

// ─── Safe error mapper — never echoes raw Postgres error text. ─────

function safeRpcError(error: { message: string; code?: string } | null): string {
  if (!error) return 'Unknown error';
  if (error.code === '42501') return 'You do not have permission to perform that action.';
  // Strip any leading SQLSTATE / context prefix so the operator-readable
  // half of `RAISE EXCEPTION` is what the toast shows.
  const msg = (error.message ?? '').trim();
  // The J6 / J7 RPCs raise messages prefixed with the function name;
  // keep them — they're operator-readable. Strip stack noise if present.
  return msg.length === 0 ? 'Action failed.' : msg;
}

// ─── List-state helper ────────────────────────────────────────────

function authTypeBadgeLabel(authType: string): string {
  switch (authType) {
    case 'hmac_sha256':
      return 'HMAC-SHA256';
    case 'signed_jwt':
      return 'Signed JWT';
    case 'manual_import':
      return 'Manual import';
    default:
      return authType;
  }
}

function rotationDisabledReason(authType: string): string | null {
  if (authType === 'hmac_sha256') return null;
  if (authType === 'manual_import') {
    return 'Manual import providers do not use a network secret, so secret rotation is not applicable.';
  }
  if (authType === 'signed_jwt') {
    return 'Signed JWT key rotation is a separate flow, currently in planning.';
  }
  return 'This provider auth type does not support secret rotation.';
}

// ═══════════════════════════════════════════════════════════════════
// Top-level panel
// ═══════════════════════════════════════════════════════════════════

export function TrainingProvidersAdminPanel({
  orgId,
  shopId,
  currentUserId,
}: TrainingProvidersAdminPanelProps) {
  const queryClient = useQueryClient();

  const [includeInactive, setIncludeInactive] = useState<boolean>(true);
  const [unmatchedOnly, setUnmatchedOnly] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const enabled = Boolean(orgId);

  const listQuery = useQuery({
    queryKey: ['training-providers-admin', orgId ?? null, shopId ?? null, includeInactive],
    enabled,
    queryFn: async (): Promise<TrainingProviderAdminRow[]> => {
      const rpc = supabase.rpc.bind(supabase) as unknown as ListProvidersRpc;
      const { data, error } = await rpc('list_training_providers_for_admin', {
        p_org_id: orgId as string,
        p_shop_id: shopId ?? null,
        p_include_inactive: includeInactive,
      });
      if (error) throw new Error(safeRpcError(error));
      return data ?? [];
    },
  });

  const refetchList = () => {
    queryClient.invalidateQueries({
      queryKey: ['training-providers-admin', orgId ?? null, shopId ?? null, includeInactive],
    });
  };

  // ── Active-state dialog state ─────────────────────────────────────
  const [activeDialog, setActiveDialog] = useState<{
    provider: TrainingProviderAdminRow;
    nextActive: boolean;
  } | null>(null);
  const [activeReason, setActiveReason] = useState<string>('');
  const [overridePendingJobs, setOverridePendingJobs] = useState<boolean>(false);

  const setActiveMutation = useMutation({
    mutationFn: async (args: {
      providerId: string;
      isActive: boolean;
      reason: string | null;
      override: boolean;
    }) => {
      if (!currentUserId) throw new Error('Sign in required.');
      const rpc = supabase.rpc.bind(supabase) as unknown as SetActiveStateRpc;
      const { error } = await rpc('set_training_provider_active_state', {
        p_provider_id: args.providerId,
        p_is_active: args.isActive,
        p_actor_user_id: currentUserId,
        p_reason: args.reason,
        p_override_pending_jobs: args.override,
      });
      if (error) throw new Error(safeRpcError(error));
    },
    onSuccess: (_data, vars) => {
      toast.success(
        vars.isActive ? 'Provider activated.' : 'Provider deactivated.',
      );
      refetchList();
      setActiveDialog(null);
      setActiveReason('');
      setOverridePendingJobs(false);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  // ── Secret-rotation dialog state ─────────────────────────────────
  const [rotateDialog, setRotateDialog] = useState<TrainingProviderAdminRow | null>(null);
  const [newSecretRef, setNewSecretRef] = useState<string>('');
  const [overlapWindow, setOverlapWindow] = useState<number>(0);
  const [rotateReason, setRotateReason] = useState<string>('');

  const rotateMutation = useMutation({
    mutationFn: async (args: {
      providerId: string;
      newSecretRef: string;
      overlap: number;
      reason: string | null;
    }) => {
      if (!currentUserId) throw new Error('Sign in required.');
      // Client-side clamp 0..3600 — server clamps too, but echo the
      // bound to the operator before submission.
      const safeOverlap = Math.min(Math.max(args.overlap | 0, 0), 3600);
      const rpc = supabase.rpc.bind(supabase) as unknown as RotateSecretRpc;
      const { error } = await rpc('rotate_training_provider_secret_ref', {
        p_provider_id: args.providerId,
        p_new_secret_ref: args.newSecretRef,
        p_overlap_window_seconds: safeOverlap,
        p_actor_user_id: currentUserId,
        p_reason: args.reason,
      });
      if (error) throw new Error(safeRpcError(error));
    },
    onSuccess: () => {
      toast.success('Provider secret reference rotated.');
      refetchList();
      setRotateDialog(null);
      setNewSecretRef('');
      setOverlapWindow(0);
      setRotateReason('');
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  // ── Filtered list (client-side filters layered on the J3 result) ─
  const visibleProviders = useMemo(() => {
    const all = listQuery.data ?? [];
    const q = searchQuery.trim().toLowerCase();
    return all.filter((p) => {
      if (unmatchedOnly && !p.has_unmatched_events) return false;
      if (q.length === 0) return true;
      return (
        p.display_name.toLowerCase().includes(q) ||
        p.provider_key.toLowerCase().includes(q)
      );
    });
  }, [listQuery.data, searchQuery, unmatchedOnly]);

  // ── Render branches ──────────────────────────────────────────────

  if (!enabled) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          You do not have access to provider admin.
        </CardContent>
      </Card>
    );
  }

  if (listQuery.isLoading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading providers…
        </CardContent>
      </Card>
    );
  }

  if (listQuery.error) {
    const err = listQuery.error as Error;
    const permissionDenied =
      /permission/i.test(err.message) || /42501/.test(err.message);
    return (
      <Card>
        <CardContent
          className="py-12 text-center text-sm text-muted-foreground"
          data-testid="training-provider-list-error"
        >
          {permissionDenied
            ? 'You do not have access to provider admin.'
            : 'Unable to load providers right now.'}
        </CardContent>
      </Card>
    );
  }

  const totalCount = listQuery.data?.length ?? 0;
  if (totalCount === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Training Providers</CardTitle>
          <CardDescription>
            No providers configured yet for this org.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="training-providers-admin-panel">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Training Providers</CardTitle>
          <CardDescription>
            Provider registry, integration status, and operational actions.
            Provider attestation never satisfies manager verification.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3 pb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="training-provider-search"
              placeholder="Search by display name or provider key…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch
              data-testid="training-provider-include-inactive"
              checked={includeInactive}
              onCheckedChange={(v) => setIncludeInactive(Boolean(v))}
            />
            Include inactive
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch
              data-testid="training-provider-unmatched-only"
              checked={unmatchedOnly}
              onCheckedChange={(v) => setUnmatchedOnly(Boolean(v))}
            />
            Has unmatched events
          </label>
        </CardContent>
      </Card>

      <ul data-testid="training-provider-list" className="space-y-3">
        {visibleProviders.map((p) => (
          <ProviderRow
            key={p.provider_id}
            provider={p}
            onRequestActiveToggle={(nextActive) => {
              setActiveReason('');
              setOverridePendingJobs(false);
              setActiveDialog({ provider: p, nextActive });
            }}
            onRequestRotateSecret={() => {
              setNewSecretRef('');
              setOverlapWindow(0);
              setRotateReason('');
              setRotateDialog(p);
            }}
          />
        ))}
      </ul>

      {/* Active / inactive dialog */}
      <Dialog
        open={activeDialog !== null}
        onOpenChange={(open) => {
          if (!open) {
            setActiveDialog(null);
            setActiveReason('');
            setOverridePendingJobs(false);
          }
        }}
      >
        <DialogContent data-testid="training-provider-deactivate-dialog">
          {activeDialog && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {activeDialog.nextActive ? 'Activate provider' : 'Deactivate provider'}
                </DialogTitle>
                <DialogDescription>
                  {activeDialog.nextActive
                    ? 'Re-enable inbound provider callbacks for this provider.'
                    : 'Deactivation is reversible and preserves mappings and event history.'}
                </DialogDescription>
              </DialogHeader>

              {!activeDialog.nextActive && activeDialog.provider.has_unmatched_events && (
                <div
                  className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm flex items-start gap-2"
                  data-testid="training-provider-pending-events-warning"
                >
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                  <span>
                    This provider has unmatched events. Operators can no longer
                    triage them once the provider is inactive. Check the
                    "Deactivate anyway" box and provide a reason to proceed.
                  </span>
                </div>
              )}

              {!activeDialog.nextActive && (
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    data-testid="training-provider-override-pending-jobs"
                    checked={overridePendingJobs}
                    onCheckedChange={(v) => setOverridePendingJobs(Boolean(v))}
                  />
                  Deactivate anyway (override pending jobs)
                </label>
              )}

              <div className="space-y-2">
                <Label htmlFor="training-provider-deactivate-reason">
                  Reason
                  {(overridePendingJobs || !activeDialog.nextActive) && (
                    <span className="text-destructive"> *</span>
                  )}
                </Label>
                <Textarea
                  id="training-provider-deactivate-reason"
                  data-testid="training-provider-deactivate-reason"
                  placeholder="Operational note (visible in the admin audit log)"
                  value={activeReason}
                  onChange={(e) => setActiveReason(e.target.value)}
                />
              </div>

              <DialogFooter>
                {activeDialog.nextActive ? (
                  <Button
                    data-testid="training-provider-activate-confirm"
                    disabled={setActiveMutation.isPending}
                    onClick={() =>
                      setActiveMutation.mutate({
                        providerId: activeDialog.provider.provider_id,
                        isActive: true,
                        reason: activeReason.trim() || null,
                        override: false,
                      })
                    }
                  >
                    {setActiveMutation.isPending && (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    )}
                    Activate
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    data-testid="training-provider-deactivate-confirm"
                    disabled={
                      setActiveMutation.isPending ||
                      (overridePendingJobs && activeReason.trim().length === 0)
                    }
                    onClick={() =>
                      setActiveMutation.mutate({
                        providerId: activeDialog.provider.provider_id,
                        isActive: false,
                        reason: activeReason.trim() || null,
                        override: overridePendingJobs,
                      })
                    }
                  >
                    {setActiveMutation.isPending && (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    )}
                    Deactivate
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Secret rotation dialog */}
      <Dialog
        open={rotateDialog !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRotateDialog(null);
            setNewSecretRef('');
            setOverlapWindow(0);
            setRotateReason('');
          }
        }}
      >
        <DialogContent data-testid="training-provider-rotate-secret-dialog">
          {rotateDialog && (
            <>
              <DialogHeader>
                <DialogTitle>Rotate secret reference</DialogTitle>
                <DialogDescription>
                  Paste the opaque secret reference issued by the out-of-band
                  secret manager. The raw secret material is never stored or
                  displayed here.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-2">
                <Label htmlFor="training-provider-rotate-secret-ref-input">
                  Opaque secret reference
                </Label>
                <Input
                  id="training-provider-rotate-secret-ref-input"
                  data-testid="training-provider-rotate-secret-ref-input"
                  placeholder="ref-…"
                  value={newSecretRef}
                  onChange={(e) => setNewSecretRef(e.target.value)}
                  autoComplete="off"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="training-provider-rotate-secret-overlap-input">
                  Overlap window (seconds)
                </Label>
                <Input
                  id="training-provider-rotate-secret-overlap-input"
                  data-testid="training-provider-rotate-secret-overlap-input"
                  type="number"
                  min={0}
                  max={3600}
                  value={overlapWindow}
                  onChange={(e) => {
                    const raw = Number(e.target.value);
                    if (!Number.isFinite(raw)) {
                      setOverlapWindow(0);
                      return;
                    }
                    setOverlapWindow(Math.min(Math.max(Math.floor(raw), 0), 3600));
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Clamped to 0..3600 seconds.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="training-provider-rotate-secret-reason">
                  Reason <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="training-provider-rotate-secret-reason"
                  data-testid="training-provider-rotate-secret-reason"
                  placeholder="Operational note (visible in the admin audit log)"
                  value={rotateReason}
                  onChange={(e) => setRotateReason(e.target.value)}
                />
              </div>

              <DialogFooter>
                <Button
                  data-testid="training-provider-rotate-secret-confirm"
                  disabled={
                    rotateMutation.isPending ||
                    newSecretRef.trim().length === 0 ||
                    rotateReason.trim().length === 0
                  }
                  onClick={() =>
                    rotateMutation.mutate({
                      providerId: rotateDialog.provider_id,
                      newSecretRef: newSecretRef.trim(),
                      overlap: overlapWindow,
                      reason: rotateReason.trim() || null,
                    })
                  }
                >
                  {rotateMutation.isPending && (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  )}
                  Rotate
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Per-row component
// ═══════════════════════════════════════════════════════════════════

interface ProviderRowProps {
  provider: TrainingProviderAdminRow;
  onRequestActiveToggle: (nextActive: boolean) => void;
  onRequestRotateSecret: () => void;
}

function ProviderRow({
  provider,
  onRequestActiveToggle,
  onRequestRotateSecret,
}: ProviderRowProps) {
  const disabledRotationReason = rotationDisabledReason(provider.auth_type);
  return (
    <li
      data-testid="training-provider-row"
      className="rounded-lg border bg-card p-4 space-y-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-medium truncate">
              {/* Provider display name */}
              {provider.display_name}
            </h4>
            <Badge
              variant={provider.is_active ? 'default' : 'outline'}
              data-testid="training-provider-status-badge"
              className="text-xs"
            >
              {provider.is_active ? 'Active' : 'Inactive'}
            </Badge>
            <Badge
              variant="secondary"
              data-testid="training-provider-auth-badge"
              className="text-xs"
            >
              {authTypeBadgeLabel(provider.auth_type)}
            </Badge>
            <Badge
              variant={provider.secret_configured ? 'default' : 'outline'}
              data-testid="training-provider-secret-status"
              className="text-xs"
            >
              {provider.secret_configured ? 'Secret configured' : 'Secret not configured'}
            </Badge>
            <Badge
              variant={provider.public_key_configured ? 'default' : 'outline'}
              data-testid="training-provider-public-key-status"
              className="text-xs"
            >
              {provider.public_key_configured ? 'Public key configured' : 'Public key not configured'}
            </Badge>
            {provider.has_unmatched_events && (
              <Badge
                variant="destructive"
                data-testid="training-provider-unmatched-badge"
                className="text-xs"
              >
                Has unmatched events
              </Badge>
            )}
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <div className="flex gap-2">
              <dt>Provider key:</dt>
              <dd className="font-mono">{provider.provider_key}</dd>
            </div>
            <div className="flex gap-2">
              <dt>Provider type:</dt>
              <dd>{provider.provider_type}</dd>
            </div>
            <div className="flex gap-2">
              <dt>Auth type:</dt>
              <dd>{authTypeBadgeLabel(provider.auth_type)}</dd>
            </div>
            <div className="flex gap-2">
              <dt>Scope:</dt>
              <dd>{provider.shop_id ? 'shop-level' : 'org-level'}</dd>
            </div>
            <div className="flex gap-2">
              <dt>Active state:</dt>
              <dd>{provider.is_active ? 'Active' : 'Inactive'}</dd>
            </div>
            <div className="flex gap-2">
              <dt>Last received event:</dt>
              <dd>
                {provider.last_received_at
                  ? provider.last_received_at
                  : 'No events yet'}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt>Event count:</dt>
              <dd>{String(provider.event_count ?? 0)}</dd>
            </div>
            <div className="flex gap-2">
              <dt>Has unmatched events:</dt>
              <dd>{provider.has_unmatched_events ? 'Yes' : 'No'}</dd>
            </div>
          </dl>
        </div>

        <div className="flex flex-col items-end gap-2 shrink-0">
          {/* Actions */}
          <Button
            size="sm"
            variant={provider.is_active ? 'outline' : 'default'}
            data-testid="training-provider-active-toggle"
            onClick={() => onRequestActiveToggle(!provider.is_active)}
          >
            {provider.is_active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid="training-provider-rotate-secret-action"
            disabled={disabledRotationReason !== null}
            title={disabledRotationReason ?? 'Rotate the opaque secret reference'}
            onClick={() => {
              if (disabledRotationReason === null) onRequestRotateSecret();
            }}
          >
            <KeyRound className="h-4 w-4 mr-1" />
            Rotate secret reference
          </Button>
          {disabledRotationReason && (
            <p
              className="text-xs text-muted-foreground max-w-[220px] text-right"
              data-testid="training-provider-rotate-secret-disabled-reason"
            >
              {disabledRotationReason}
            </p>
          )}
        </div>
      </div>

      <IntegrationInstructions provider={provider} />
    </li>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Integration instructions (callback endpoint + header names + key)
// ═══════════════════════════════════════════════════════════════════

function IntegrationInstructions({ provider }: { provider: TrainingProviderAdminRow }) {
  return (
    <section
      data-testid="training-provider-integration-instructions"
      className="rounded-md border bg-muted/30 p-3 space-y-2 text-xs"
    >
      <h5 className="text-xs font-medium">Integration instructions</h5>
      <div className="flex flex-col gap-1">
        <div className="flex gap-2">
          <span className="text-muted-foreground">Endpoint:</span>
          <code className="font-mono">record-training-provider-evidence</code>
        </div>
        <div className="flex gap-2">
          <span className="text-muted-foreground">Provider key:</span>
          <code className="font-mono">{provider.provider_key}</code>
        </div>
        <div>
          <span className="text-muted-foreground">Required headers:</span>
          <ul className="list-disc pl-5 mt-1 space-y-0.5 font-mono">
            <li>X-OptiBay-Org-Id</li>
            <li>X-OptiBay-Provider</li>
            <li>X-OptiBay-Signature</li>
            <li>X-OptiBay-Timestamp</li>
            <li>X-OptiBay-Event-Id</li>
          </ul>
        </div>
        <p className="text-muted-foreground">
          Compute the signature over the request body and the X-OptiBay-Timestamp
          value using the per-provider HMAC secret. The Edge Function tolerates
          clock skew up to {provider.timestamp_tolerance_seconds} seconds.
        </p>
        <div>
          <span className="text-muted-foreground">Sample payload skeleton:</span>
          <pre className="font-mono mt-1 rounded bg-background/60 p-2 overflow-x-auto">
{`{
  "provider_event_id": "<unique-id>",
  "event_type": "completion",
  "external_course_id": "<course-id>",
  "external_user_id": "<user-id>",
  "external_user_email": "<verified-email>",
  "external_user_email_verified": true,
  "attested_at": "<ISO-8601-timestamp>"
}`}
          </pre>
        </div>
      </div>
    </section>
  );
}
